import { apiClient } from '../../lib/api/apiClient';
import { ApiError } from '../../lib/api/types';
import { ApiContractError, isRecord } from '../../lib/api/wire';

/**
 * The mailbox seam's wire shapes and calls, transcribed READ-ONLY from three frozen contracts:
 *
 * - F20-a's Wave-1 contract (`.forge/plans/F20-a/F20-a-Wave-1-contract.md`) — `MailboxConnectionResponse`,
 *   `DELETE /connection` and the stub surface;
 * - F20-b's Wave-2 contract (`.forge/plans/F20-b/F20-b-Wave-2-contract.md`) — `lastSyncedAt` and
 *   `subscription.reasonCode`, whose sole authority it is;
 * - **F20-i's Wave-1 contract (`.forge/plans/F20-i/F20-i-Wave-1-contract.md`)** — the connection check's
 *   `200` `MailboxConnectionCheckResponse` (`connection`, `lastRelease`, `nextStep`), which replaces F20-a's
 *   `404 MAILBOX_NOT_CONNECTED` on `GET /connection` (F20-i spec FR-16), and the stub connect's optional
 *   `StubConnectRequest` with its `409 MAILBOX_CONNECT_FAILED` and `reason`.
 *
 * The server halves are other work items; a discovered divergence from this transcription is a
 * re-decompose and a contract re-author, never an edit here (each contract's § Read-Only to Implementers).
 *
 * Transcribed rather than generated: no OpenAPI codegen is wired (F20-i contract § Client-side facts,
 * item 3), the same way F5-a's `MeResponse` was transcribed into `src/lib/api/types.ts`. The bodies
 * are narrowed from `unknown` rather than asserted through a generic, so a shape divergence fails
 * loudly at the boundary instead of surfacing as `undefined` inside a render — the `unknown`-and-
 * narrow shape of `apiClient`'s own `toProblem`.
 *
 * Feature-local on purpose: only `src/features/email` consumes these endpoints, and a feature owns
 * its own internals (`forge-et-fe/CLAUDE.md § Layout` — a promotion to `src/lib` is what two
 * features sharing a shape would force, and nothing else does).
 */

/** `GET`/`DELETE` the caller's own connection. */
export const MAILBOX_CONNECTION_PATH = '/api/v1/mailbox/connection';
/**
 * `local`/`test` only — the stub connect a connect button calls on a laptop (F20-a spec AC-22a), and the
 * CRM's automatic connect calls with `mode: AUTOMATIC` (F20-i spec FR-23).
 */
export const MAILBOX_STUB_CONNECT_PATH = '/api/v1/mailbox/stub-connect';
/** `local`/`test` only — the state-forcing surface (F20-a spec FR-13). */
export const MAILBOX_STUB_STATE_PATH = '/api/v1/mailbox/stub-state';

/**
 * The problem `code` the **mail endpoints** answer when the caller has no usable connection
 * (`inboxApi.ts` and the send and message reads). It never appears on `GET /connection`,
 * which answers `200` with `connection: null` instead (F20-i contract § Problem envelope); a `404`
 * there, of either code, is an ordinary error.
 */
export const MAILBOX_NOT_CONNECTED = 'MAILBOX_NOT_CONNECTED';

/**
 * `DISCONNECTED` never appears on the wire — a disconnected or absent connection is the check's
 * `connection: null`, so the client's state machine is three-way, never four.
 */
const MAILBOX_STATUSES = ['ACTIVE', 'RECONNECT_REQUIRED'] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

/**
 * The closed `RECONNECT_REQUIRED` vocabulary (F20-a spec FR-6). Carried as a value so `StubStateRequest`
 * and the reason-copy map read from one list.
 *
 * The *wire* field is deliberately typed `string | null` rather than this union: the contract
 * obliges the client to tolerate an unrecognised value by falling back to its generic reconnect
 * copy rather than rendering the token, and a union-typed field would either reject the response or
 * lie about what arrived.
 */
export const MAILBOX_STATUS_REASONS = [
    'CONSENT_REVOKED',
    'TOKEN_EXPIRED',
    'INTERACTION_REQUIRED',
    'SCOPES_CHANGED',
    'MAILBOX_NOT_ENABLED',
] as const;
export type MailboxStatusReason = (typeof MAILBOX_STATUS_REASONS)[number];

/**
 * `MailboxConnectionResponse.subscription` — always present. "No subscription" is `status: NONE`,
 * not a null object.
 *
 * **F20-b's sync indicator renders from `status` and `reasonCode`** (F20-b-WI-2.2, against the frozen
 * `.forge/plans/F20-b/F20-b-Wave-2-contract.md`, which added `reasonCode`); `syncState.ts` resolves
 * them into the Healthy / Waiting / Lapsed states both surfaces show. `status` and `reasonCode` are
 * both carried as a `string` rather than a union for the reason `statusReason` is: the contract
 * obliges the client to tolerate an unrecognised value with generic copy, and a union-typed field
 * would either reject the whole response or lie about what arrived. The membership check happens at
 * render time, in `syncState.ts`, never at parse time.
 *
 * `reason` stays free provider text and is **never rendered** — `reasonCode` is the closed code for
 * rendering, `reason` the diagnostic (F20-b Wave-2 contract § Field Definitions). `expiresAt` is
 * transcribed and not rendered either: the contract carries it, and no acceptance criterion asks
 * for it.
 */
export interface MailboxSubscription {
    /** `ACTIVE` | `FAILED` | `NONE` — see the note above on why this is not a union. */
    readonly status: string;
    /** `null` unless `status` is `ACTIVE`. Transcribed, not rendered. */
    readonly expiresAt: string | null;
    /** Free text, diagnostic only, set when `status` is `FAILED`. Never rendered. */
    readonly reason: string | null;
    /**
     * `RENEWAL_FAILED` | `EXPIRED` | `SUBSCRIPTION_GONE` — `null` whenever `status` is `ACTIVE` or
     * `NONE`, non-null whenever it is `FAILED` (F20-b Wave-2 contract). A `string`, not a union — see above.
     */
    readonly reasonCode: string | null;
}

/**
 * The connection inside the check's `200` body, and the `200` body of `POST /stub-connect` and
 * `POST /stub-state` — one type, so the client has one parser and one set of render branches.
 *
 * Nullable members are **present with `null`**, never an omitted key (F20-a contract § Envelope Rules),
 * so they are declared `T | null` and not `T?` — `exactOptionalPropertyTypes` makes those two
 * different things, and the distinction is the one the contract went out of its way to fix.
 *
 * **No `id` member and no token material of any kind** — both are properties of the type rather
 * than of a particular response (F20-a spec FR-10, AC-16, NFR-1). A field carrying either must never be
 * added here.
 */
export interface MailboxConnectionResponse {
    readonly status: MailboxStatus;
    /** The connected mailbox's primary address. */
    readonly mailboxAddress: string;
    /** Nullable deliberately: Graph's `/me` may resolve an identity with no display name. */
    readonly displayName: string | null;
    /** ISO-8601 UTC instant, `Z` suffix. Localising it is the client's job. */
    readonly connectedAt: string;
    /** ISO-8601 UTC instant; `null` on a connection whose token has never been refreshed. */
    readonly lastRefreshedAt: string | null;
    /** `null` whenever `status` is `ACTIVE`; one of `MAILBOX_STATUS_REASONS` when it is not. */
    readonly statusReason: string | null;
    /** The normalised Graph permission set — `[]`, never `null`. Order is not guaranteed. */
    readonly grantedScopes: readonly string[];
    readonly subscription: MailboxSubscription;
    /**
     * ISO-8601 UTC instant — when mail last successfully arrived, by ingest or delta poll. `null`
     * until the first one succeeds, which includes every connection just after connect (Wave-2
     * contract). The sync indicator's Healthy state reads THIS, never the connection's `status`.
     */
    readonly lastSyncedAt: string | null;
}

/** The `local`/`test` state-forcing actions (contract `StubStateRequest`). */
const STUB_STATE_ACTIONS = ['FORCE_RECONNECT_REQUIRED', 'FAIL_NEXT_REFRESH'] as const;
export type StubStateAction = (typeof STUB_STATE_ACTIONS)[number];

/** The body of `POST /api/v1/mailbox/stub-state`. Both members are required. */
export interface StubStateRequest {
    readonly action: StubStateAction;
    /** The closed `RECONNECT_REQUIRED` vocabulary — the two disconnect reasons are not accepted. */
    readonly reason: MailboxStatusReason;
}

/**
 * What the CRM does next about the mailbox (F20-i spec FR-16; contract § `nextStep` rules), worked out by
 * the server on every call. The client renders from it and `connection.status` only, and never compares
 * scopes itself.
 */
export const MAILBOX_NEXT_STEPS = ['NONE', 'CONNECT_NOW', 'TRY_AGAIN', 'RECONNECT'] as const;
export type MailboxNextStep = (typeof MAILBOX_NEXT_STEPS)[number];

/**
 * `MailboxConnectionCheckResponse.lastRelease` — the caller's most recent released connection.
 *
 * `reason` (`USER_DISCONNECTED` | `OFFBOARDED` | `PERMISSION_REMOVED`) is carried as a `string`: wave 1
 * renders nothing from it — the client renders by `nextStep` — so it is transcribed, not membership-checked.
 */
export interface MailboxLastRelease {
    readonly reason: string;
    /** ISO-8601 UTC instant, `Z` suffix. */
    readonly releasedAt: string;
}

/**
 * What a connection read resolved to.
 *
 * Two kinds, kept from F20-a so the shipped consumers (`MessageView`, `ComposeEmailDialog`,
 * `composeConnectionView`) compile unchanged (F20-i WI-1.3 plan D-1): the check's `connection: null` is
 * `not-connected` and a connection is `connected`, and **both carry `nextStep` and `lastRelease`**. Every
 * refusal of the read — a `404` of either code included — propagates as a thrown error.
 */
export type MailboxConnectionResult =
    | {
          readonly kind: 'connected';
          readonly connection: MailboxConnectionResponse;
          readonly nextStep: MailboxNextStep;
          readonly lastRelease: MailboxLastRelease | null;
      }
    | {
          readonly kind: 'not-connected';
          readonly nextStep: MailboxNextStep;
          readonly lastRelease: MailboxLastRelease | null;
      };

/** `StubConnectRequest.mode` — the optional body of `POST /api/v1/mailbox/stub-connect` (F20-i contract). */
export type StubConnectMode = 'AUTOMATIC' | 'INTERACTIVE';

/**
 * The problem `code` of a forced connect failure on the stub path (`409`, F20-i contract
 * `MAILBOX_CONNECT_FAILED`), which the client handles exactly as the `mailboxConnect=failed` marker.
 */
export const MAILBOX_CONNECT_FAILED = 'MAILBOX_CONNECT_FAILED';

/**
 * What a stub connect resolved to: the connection, or the forced failure with its `reason` (the
 * `mailboxConnectReason` vocabulary, or `null`). The reason is a `string`: an unrecognised value falls back
 * to the message without a reason where the copy is chosen (`connect/connectMarkers.ts`).
 */
export type StubConnectResult =
    | { readonly kind: 'connected'; readonly connection: MailboxConnectionResponse }
    | { readonly kind: 'failed'; readonly reason: string | null };

/**
 * A response body that does not match the contract. Distinct from an `ApiError`, which is a refusal.
 * A subclass of `src/lib/api/wire.ts`'s `ApiContractError`, which the shared `page` narrowing throws.
 */
export class MailboxContractError extends ApiContractError {
    constructor(detail: string) {
        // "the mailbox contract", not "the F20-b Wave-1 contract": the shape now spans F20-a's Wave-1 and
        // F20-b's Wave-2 contracts, so naming one would point a reader at the wrong document.
        super(`The mailbox response did not match the mailbox contract: ${detail}`);
        this.name = 'MailboxContractError';
    }
}

function requireString(value: unknown, field: string): string {
    if (typeof value !== 'string') {
        throw new MailboxContractError(`${field} must be a string`);
    }
    return value;
}

/** A present-with-`null` member: the value, or `null`. An omitted key is a divergence, not a null. */
function requireNullableString(value: unknown, field: string): string | null {
    if (value === null) {
        return null;
    }
    if (typeof value !== 'string') {
        throw new MailboxContractError(`${field} must be a string or null, present either way`);
    }
    return value;
}

/**
 * A type predicate rather than a cast on the value: widening the *list* to `readonly string[]` is
 * always true, whereas asserting the *value* into the union is the unchecked claim this module
 * exists to avoid.
 */
function isMailboxStatus(value: string): value is MailboxStatus {
    return (MAILBOX_STATUSES as readonly string[]).includes(value);
}

function toSubscription(value: unknown): MailboxSubscription {
    if (!isRecord(value)) {
        throw new MailboxContractError('subscription must be an object, never null');
    }
    return {
        status: requireString(value.status, 'subscription.status'),
        expiresAt: requireNullableString(value.expiresAt, 'subscription.expiresAt'),
        reason: requireNullableString(value.reason, 'subscription.reason'),
        reasonCode: requireNullableString(value.reasonCode, 'subscription.reasonCode'),
    };
}

/**
 * Narrow a parsed body to a `MailboxConnectionResponse` — from `unknown`, never a cast.
 *
 * `status` is checked against the contract's two values because it is the render discriminator: a
 * third value would silently fall through the screen's state machine, and the contract pins exactly
 * two. `statusReason` and `subscription.status` are deliberately not membership-checked — see their
 * declarations above.
 */
function toMailboxConnection(value: unknown): MailboxConnectionResponse {
    if (!isRecord(value)) {
        throw new MailboxContractError('the body must be an object');
    }
    const status = requireString(value.status, 'status');
    if (!isMailboxStatus(status)) {
        throw new MailboxContractError(`status must be one of ${MAILBOX_STATUSES.join(', ')}, got ${status}`);
    }
    if (!Array.isArray(value.grantedScopes)) {
        throw new MailboxContractError('grantedScopes must be an array, never null');
    }
    return {
        // Narrowed by the predicate above, so this is the checked value, not an assertion about an
        // unchecked one.
        status,
        mailboxAddress: requireString(value.mailboxAddress, 'mailboxAddress'),
        displayName: requireNullableString(value.displayName, 'displayName'),
        connectedAt: requireString(value.connectedAt, 'connectedAt'),
        lastRefreshedAt: requireNullableString(value.lastRefreshedAt, 'lastRefreshedAt'),
        statusReason: requireNullableString(value.statusReason, 'statusReason'),
        grantedScopes: value.grantedScopes.map((scope, index) =>
            requireString(scope, `grantedScopes[${index}]`),
        ),
        subscription: toSubscription(value.subscription),
        lastSyncedAt: requireNullableString(value.lastSyncedAt, 'lastSyncedAt'),
    };
}

function isNextStep(value: string): value is MailboxNextStep {
    return (MAILBOX_NEXT_STEPS as readonly string[]).includes(value);
}

/** A member the contract makes present-with-`null`: an omitted key is a divergence, not a null. */
function requireKey(value: Record<string, unknown>, key: string): unknown {
    if (!(key in value)) {
        throw new MailboxContractError(`${key} must be present, null or not`);
    }
    return value[key];
}

function toLastRelease(value: unknown): MailboxLastRelease | null {
    if (value === null) {
        return null;
    }
    if (!isRecord(value)) {
        throw new MailboxContractError('lastRelease must be an object or null');
    }
    return {
        reason: requireString(value.reason, 'lastRelease.reason'),
        releasedAt: requireString(value.releasedAt, 'lastRelease.releasedAt'),
    };
}

/**
 * `GET /api/v1/mailbox/connection` — the connection check (F20-i spec FR-16): always `200` for a caller
 * with `email.manage`, with the connection or `null`, the last release and the next step. Every refusal
 * throws, a `404` of either code included (F20-i WI-1.3 plan D-3).
 *
 * An unrecognised `nextStep` is read as `TRY_AGAIN` (contract § `nextStep` rules) — the safe reading: it
 * never starts a connect by itself and always offers one. A missing `connection`, `lastRelease` or
 * `nextStep` key is a contract divergence (present-with-`null`, F20-i WI-1.3 plan D-2).
 */
export async function getConnection(): Promise<MailboxConnectionResult> {
    const body = await apiClient.get<unknown>(MAILBOX_CONNECTION_PATH);
    if (!isRecord(body)) {
        throw new MailboxContractError('the body must be an object');
    }
    const connection = requireKey(body, 'connection');
    const rawNextStep = requireString(requireKey(body, 'nextStep'), 'nextStep');
    const nextStep: MailboxNextStep = isNextStep(rawNextStep) ? rawNextStep : 'TRY_AGAIN';
    const lastRelease = toLastRelease(requireKey(body, 'lastRelease'));
    return connection === null
        ? { kind: 'not-connected', nextStep, lastRelease }
        : { kind: 'connected', connection: toMailboxConnection(connection), nextStep, lastRelease };
}

/**
 * `DELETE /api/v1/mailbox/connection` — idempotent, `204 No Content` either way, so there is no
 * body to parse (F20-a contract § Client-side facts, item 2).
 */
export async function disconnect(): Promise<void> {
    await apiClient.delete(MAILBOX_CONNECTION_PATH);
}

/**
 * `POST /api/v1/mailbox/stub-connect` — `local`/`test` only.
 *
 * With no `mode` it sends no body, so it is F20-a's interactive call unchanged; `AUTOMATIC` sends
 * `{"mode":"AUTOMATIC"}`, which records the automatic attempt in the session before the forced outcome
 * applies (F20-i contract `StubConnectRequest`). A second call on an existing connection updates that row,
 * which is what makes it the right call for *Try again* and *Reconnect* too.
 *
 * `200` is the connection; `409 MAILBOX_CONNECT_FAILED` is the forced failure with the problem's `reason`
 * (present-with-`null`; a non-string reads as `null`). Every other refusal throws.
 */
export async function stubConnect(mode?: StubConnectMode): Promise<StubConnectResult> {
    try {
        const body =
            mode === undefined
                ? await apiClient.post<unknown>(MAILBOX_STUB_CONNECT_PATH)
                : await apiClient.post<unknown>(MAILBOX_STUB_CONNECT_PATH, { mode });
        return { kind: 'connected', connection: toMailboxConnection(body) };
    } catch (caught) {
        if (caught instanceof ApiError && caught.status === 409 && caught.code === MAILBOX_CONNECT_FAILED) {
            const reason = caught.problem?.extensions?.reason;
            return { kind: 'failed', reason: typeof reason === 'string' ? reason : null };
        }
        throw caught;
    }
}

/**
 * `POST /api/v1/mailbox/stub-state` — `local`/`test` only.
 *
 * **No production call site, deliberately.** F20-a WI-1.3 and WI-3.1 drive the state-forcing surface from
 * Playwright's `request`, not through the SPA, so nothing on this screen invokes it; it is
 * transcribed and unit-tested here to keep the client's rendering of the frozen seam complete.
 * `pnpm knip` reports it as an unused export — expected, and advisory (knip never gates CI).
 */
export async function stubState(request: StubStateRequest): Promise<MailboxConnectionResponse> {
    const body = await apiClient.post<unknown>(MAILBOX_STUB_STATE_PATH, request);
    return toMailboxConnection(body);
}
